import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api, { patientsApi } from '../../services/api';
import { getCountyFromZip } from '../../constants/ohioZipCounty';
import { usePatientForm } from '../../hooks/usePatientForm';
import { useConfirmDialog } from '../../components/shared/ConfirmDialog';

/**
 * State + handlers for the Patients page. Extracted from the formerly-543-line
 * PatientsPage.js so the page itself becomes a thin orchestrator that wires
 * the hook into presentational components.
 */
export function usePatients() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Data
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterPayer, setFilterPayer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Modal: add / edit patient
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [initialTab, setInitialTab] = useState('basic');
  const [focusField, setFocusField] = useState(null);

  // Modal: batch import
  const [importModalOpen, setImportModalOpen] = useState(false);
  // New CSV/PDF preview-tick-commit import (patient_bulk router)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Duplicate scan + merge
  const [duplicates, setDuplicates] = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);
  const [mergeGroup, setMergeGroup] = useState(null);

  // Eligibility result dialog
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [eligibilityPatient, setEligibilityPatient] = useState(null);

  // Themed confirmation dialog (replaces native window.confirm which races
  // with Radix DropdownMenu's pointer-down close handler — clicking Delete
  // in the row kebab menu would silently swallow the confirm prompt).
  const [ConfirmDialog, openConfirm] = useConfirmDialog();

  const form = usePatientForm();
  const {
    formData, setFormData, resetForm, saving, setSaving,
    handleCheckEligibility, checkingEligibility,
  } = form;

  // ── data fetching ─────────────────────────────────────────────────────

  const fetchPatients = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterPayer) params.payer_type = filterPayer;
      if (filterStatus) params.status = filterStatus;
      const response = await patientsApi.getAll(params);
      setPatients(response.data);
    } catch {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [search, filterPayer, filterStatus]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  // Deep-link handler: ?edit=<id>&tab=<tab>&focus=<field>
  // Used by the ODM "Fix this field" button on the submission detail page.
  // P30: setter functions from useState are stable references — including
  // them in deps would only add noise. The actual triggers (patients +
  // searchParams) are listed.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || patients.length === 0) return;
    const target = patients.find((p) => p.id === editId);
    if (!target) return;
    setEditingPatient(target);
    setFormData(target);
    setInitialTab(searchParams.get('tab') || 'basic');
    setFocusField(searchParams.get('focus') || null);
    setIsModalOpen(true);
    const next = new URLSearchParams(searchParams);
    ['edit', 'tab', 'focus'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  }, [patients, searchParams, setSearchParams, setFormData]);

  // ── modal lifecycle ───────────────────────────────────────────────────

  const handleOpenModal = (patient = null) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData(patient);
    } else {
      setEditingPatient(null);
      resetForm();
    }
    setInitialTab('basic');
    setFocusField(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPatient(null);
    setInitialTab('basic');
    setFocusField(null);
    resetForm();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'zip_code' && value.length >= 5) {
      const county = getCountyFromZip(value);
      if (county) {
        setFormData((prev) => ({ ...prev, county }));
        toast.info(`County auto-detected: ${county}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Strip the client-side `_uid` fields that PatientBillingLines
      // attaches to each billing-line row for React reconciliation. The
      // backend Pydantic model doesn't recognise them and would either
      // reject or silently persist the extra field — neither is desired.
      const payload = {
        ...formData,
        billing_lines: (formData.billing_lines || []).map(
          ({ _uid: _drop, ...rest }) => rest
        ),
      };
      if (editingPatient) {
        await patientsApi.update(editingPatient.id, payload);
        toast.success('Patient updated');
      } else {
        await patientsApi.create(payload);
        toast.success('Patient added');
      }
      handleCloseModal();
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save patient');
    } finally {
      setSaving(false);
    }
  };

  // ── per-row actions ───────────────────────────────────────────────────

  const handleDelete = async (id) => {
    const ok = await openConfirm({
      title: 'Delete this patient?',
      description: 'This will permanently remove the patient record. This action cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
      testId: 'confirm-delete-patient',
    });
    if (!ok) return;
    try {
      await patientsApi.delete(id);
      toast.success('Patient deleted');
      fetchPatients();
    } catch {
      toast.error('Failed to delete patient');
    }
  };

  const handleEligibilityCheck = async (patientId) => {
    const target = patients.find((p) => p.id === patientId) || null;
    const result = await handleCheckEligibility(patientId);
    if (result) {
      setEligibilityPatient(target);
      setEligibilityResult(result);
      fetchPatients();
    }
  };

  const handleRecheckEligibility = async () => {
    if (!eligibilityPatient?.id) return;
    const result = await handleCheckEligibility(eligibilityPatient.id);
    if (result) {
      setEligibilityResult(result);
      fetchPatients();
    }
  };

  const handleCloseEligibility = () => {
    setEligibilityResult(null);
    setEligibilityPatient(null);
  };

  const handleToggleStatus = async (patient) => {
    const action = patient.status === 'Active' ? 'deactivate' : 'activate';
    try {
      await api.post(`/patients/${patient.id}/${action}`);
      toast.success(`Patient ${action}d`);
      fetchPatients();
    } catch {
      toast.error(`Failed to ${action} patient`);
    }
  };

  // ── bulk selection ────────────────────────────────────────────────────

  const isAllSelected = patients.length > 0 && selectedIds.length === patients.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < patients.length;

  const toggleSelectAll = () => {
    setSelectedIds(isAllSelected ? [] : patients.map((p) => p.id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const ok = await openConfirm({
      title: `Delete ${selectedIds.length} patient${selectedIds.length === 1 ? '' : 's'}?`,
      description: 'This will permanently remove the selected patient records. This action cannot be undone.',
      confirmText: `Delete ${selectedIds.length}`,
      destructive: true,
      testId: 'confirm-bulk-delete-patients',
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/patients/bulk-delete', { ids: selectedIds });
      toast.success(`Deleted ${res.data.deleted_count} patients`);
      setSelectedIds([]);
      fetchPatients();
    } catch {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── duplicate scan ────────────────────────────────────────────────────

  const handleScanDuplicates = async () => {
    setScanningDuplicates(true);
    try {
      const res = await api.get('/patients/duplicates');
      setDuplicates(res.data);
      setShowDuplicates(true);
      if (res.data.total_duplicate_groups === 0) {
        toast.success('No duplicates found');
      } else {
        toast.warning(`Found ${res.data.total_duplicate_groups} duplicate group(s)`);
      }
    } catch {
      toast.error('Failed to scan for duplicates');
    } finally {
      setScanningDuplicates(false);
    }
  };

  const handleSuggestSelect = (patient) => {
    // Clicking a suggestion opens the patient's record — same UX as clicking
    // "Edit" in the row actions menu. Previously this just set the search
    // input to "First Last", which (a) wasn't intuitive feedback and (b) the
    // backend search couldn't tokenize "First Last" across first_name +
    // last_name fields, so the table went empty for any multi-word match.
    //
    // The suggest endpoint returns a TRIMMED projection (no billing_lines,
    // no insurance fields, no clinical, etc.) for performance. Look up the
    // full record from the already-fetched list so the Edit modal renders
    // every tab with the right values.
    const full = patients.find((p) => p.id === patient.id) || patient;
    handleOpenModal(full);
  };

  return {
    // form pass-through
    form,
    formData,
    saving,
    checkingEligibility,
    // data
    patients,
    loading,
    fetchPatients,
    // filters
    search, setSearch, filterPayer, setFilterPayer, filterStatus, setFilterStatus,
    // modal
    isModalOpen, editingPatient, initialTab, focusField,
    handleOpenModal, handleCloseModal, handleChange, handleSubmit,
    // import modal
    importModalOpen, setImportModalOpen,
    bulkUploadOpen, setBulkUploadOpen,
    // bulk
    selectedIds, setSelectedIds, bulkDeleting,
    isAllSelected, isSomeSelected, toggleSelectAll, toggleSelect,
    handleBulkDelete,
    // per-row
    handleDelete, handleEligibilityCheck, handleToggleStatus,
    // eligibility dialog
    eligibilityResult, eligibilityPatient,
    handleCloseEligibility, handleRecheckEligibility,
    // duplicates
    duplicates, showDuplicates, setShowDuplicates,
    scanningDuplicates, handleScanDuplicates,
    mergeGroup, setMergeGroup,
    // search suggest
    handleSuggestSelect,
    // themed confirm dialog (rendered by PatientsPage)
    ConfirmDialog,
  };
}
